import { useState, useEffect } from 'react';

interface TutorialState {
  tutorialCompleted: boolean;
  completionTimestamp: number | null;
  currentStep: number;
  isFirstTime: boolean;
}

const TUTORIAL_STORAGE_KEY = 'histocracy_tutorial_state';

const getInitialTutorialState = (): TutorialState => {
  const storedState = localStorage.getItem(TUTORIAL_STORAGE_KEY);
  
  if (storedState) {
    try {
      const parsed = JSON.parse(storedState);
      return {
        tutorialCompleted: parsed.tutorialCompleted || false,
        completionTimestamp: parsed.completionTimestamp || null,
        currentStep: parsed.currentStep || 0,
        isFirstTime: false,
      };
    } catch (error) {
      console.error('Error parsing tutorial state:', error);
    }
  }
  
  return {
    tutorialCompleted: false,
    completionTimestamp: null,
    currentStep: 0,
    isFirstTime: true,
  };
};

export const useTutorial = () => {
  const [tutorialState, setTutorialState] = useState<TutorialState>(getInitialTutorialState);
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    // Save to localStorage whenever tutorial state changes
    localStorage.setItem(TUTORIAL_STORAGE_KEY, JSON.stringify(tutorialState));
  }, [tutorialState]);

  const startTutorial = () => {
    setShowTutorial(true);
    setTutorialState(prev => ({ ...prev, currentStep: 0 }));
  };

  const nextStep = () => {
    setTutorialState(prev => ({
      ...prev,
      currentStep: prev.currentStep + 1,
    }));
  };

  const previousStep = () => {
    setTutorialState(prev => ({
      ...prev,
      currentStep: Math.max(0, prev.currentStep - 1),
    }));
  };

  const skipTutorial = () => {
    setShowTutorial(false);
    setTutorialState({
      tutorialCompleted: true,
      completionTimestamp: Date.now(),
      currentStep: 0,
      isFirstTime: false,
    });
  };

  const completeTutorial = () => {
    setShowTutorial(false);
    setTutorialState({
      tutorialCompleted: true,
      completionTimestamp: Date.now(),
      currentStep: 0,
      isFirstTime: false,
    });
  };

  const resetTutorial = () => {
    localStorage.removeItem(TUTORIAL_STORAGE_KEY);
    setTutorialState({
      tutorialCompleted: false,
      completionTimestamp: null,
      currentStep: 0,
      isFirstTime: true,
    });
    setShowTutorial(false);
  };

  const shouldShowTutorial = () => {
    return tutorialState.isFirstTime || !tutorialState.tutorialCompleted;
  };

  const canSkipTutorial = () => {
    return !tutorialState.isFirstTime && tutorialState.currentStep > 0;
  };

  return {
    tutorialState,
    showTutorial,
    startTutorial,
    nextStep,
    previousStep,
    skipTutorial,
    completeTutorial,
    resetTutorial,
    shouldShowTutorial,
    canSkipTutorial,
    setShowTutorial,
  };
};
